import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { DownloadSimple, X, DeviceMobile } from "@phosphor-icons/react";

const DISMISS_KEY = "necos_pwa_dismissed_at";
const COOLDOWN_DAYS = 14;
const SHOW_AFTER_SECONDS = 120; // 2 minutes of active use

function wasDismissedRecently() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const ageDays = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
    return ageDays < COOLDOWN_DAYS;
  } catch { return false; }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator?.standalone === true;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(wasDismissedRecently());
  const [readyToShow, setReadyToShow] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  // iOS Safari doesn't fire beforeinstallprompt — we offer an iOS tip card instead
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const iosLike = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iosLike && !isStandalone());
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Only surface the prompt after the user has been actively using the app
  useEffect(() => {
    if (dismissed || installed) return;
    let elapsed = parseInt(sessionStorage.getItem("necos_active_seconds") || "0", 10);
    const tick = () => {
      if (!document.hidden) {
        elapsed += 1;
        sessionStorage.setItem("necos_active_seconds", String(elapsed));
        if (elapsed >= SHOW_AFTER_SECONDS) setReadyToShow(true);
      }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dismissed, installed]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
    } else {
      // User dismissed the native sheet — cooldown too
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      setDismissed(true);
    }
  }, [deferredPrompt]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  if (installed || dismissed || !readyToShow) return null;

  // iOS tip card (no beforeinstallprompt available on Safari)
  if (isIOS && !deferredPrompt) {
    return (
      <div className="fixed bottom-[calc(7.5rem+env(safe-area-inset-bottom))] md:bottom-4 left-3 right-3 md:left-auto md:right-4 md:w-80 z-50 animate-fade-in" data-testid="pwa-install-prompt-ios">
        <div className="rounded-xl bg-card border border-border shadow-xl overflow-hidden">
          <div className="p-4 flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <DeviceMobile size={20} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Install on your iPhone</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                Tap the <span className="font-semibold">Share</span> button, then <span className="font-semibold">"Add to Home Screen"</span>.
              </p>
            </div>
            <button onClick={handleDismiss} className="p-1 -mr-1 -mt-1 hover:bg-muted rounded text-muted-foreground"
                    data-testid="pwa-dismiss-btn"><X size={14} /></button>
          </div>
        </div>
      </div>
    );
  }

  if (!deferredPrompt) return null;

  return (
    <div className="fixed bottom-[calc(7.5rem+env(safe-area-inset-bottom))] md:bottom-4 left-3 right-3 md:left-auto md:right-4 md:w-[340px] z-50 animate-fade-in" data-testid="pwa-install-prompt">
      <div className="rounded-xl bg-gradient-to-br from-card to-card/95 border border-border shadow-xl overflow-hidden">
        <div className="p-4 flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 ring-1 ring-primary/20">
            <DownloadSimple size={22} className="text-primary" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Install the app</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">Faster load, home-screen access, and offline support. Takes one tap.</p>
          </div>
          <button onClick={handleDismiss} className="p-1 -mr-1 -mt-1 hover:bg-muted rounded text-muted-foreground"
                  data-testid="pwa-dismiss-btn"><X size={14} /></button>
        </div>
        <div className="px-4 pb-3 flex gap-2">
          <Button size="sm" className="flex-1 text-xs h-8" onClick={handleInstall} data-testid="pwa-install-btn">
            Install now
          </Button>
          <Button size="sm" variant="ghost" className="text-xs h-8" onClick={handleDismiss}>
            Later
          </Button>
        </div>
      </div>
    </div>
  );
}
